// rbacPermissions.js -- shared per-URN matrix permission utilities used by the
// useRbacPermissions hook to gate action buttons across ml, mwc and kaneai.
//
// The matrix host is read from REACT_APP_MATRIX_HOST. When unset or the call
// fails, callers treat the check as "allow" so legacy behaviour (all buttons
// shown) survives.

const NAMESPACE = 'automation';

// Amplitude flag key that gates the entire RBAC system. Shared so each
// app's adapter hook reads from the same source of truth.
export const RBAC_FLAG_KEY = 'is_rbac_enabled_cloud';

export const Verb = Object.freeze({
  READ: 'READ',
  CREATE: 'CREATE',
  UPDATE: 'UPDATE',
  DELETE: 'DELETE',
  EXECUTE: 'EXECUTE'
});

// Default timeout for the matrix permission fetch. Anything longer is treated
// as a transport failure and falls open.
const MATRIX_FETCH_TIMEOUT_MS = 5000;

export function getMatrixHost() {
  return process.env.REACT_APP_MATRIX_HOST || '';
}

// Flat URN builders. matrix's server-side hierarchical evaluator promotes
// project:* / project:<id> grants to cover children, so a leaf URN like
// `automation:build:<id>` is what we send.
export function buildUrn(buildId) {
  const id = buildId == null || buildId === '' ? '*' : String(buildId);
  return `${NAMESPACE}:build:${id}`;
}

export function testUrn(buildId, testId) {
  const bid = buildId == null || buildId === '' ? '*' : String(buildId);
  const tid = testId == null || testId === '' ? '*' : String(testId);
  return `${NAMESPACE}:build:${bid}:test_case_instance:${tid}`;
}

// Pre-computed wildcard URNs -- callers that gate at the org level (every
// build, every test_case_instance) should import these constants instead of
// calling buildUrn()/testUrn() at render time.
export const WILDCARD_BUILD_URN = buildUrn();
export const WILDCARD_TEST_URN = testUrn();

// Normalises a bearer token: returns null when missing, otherwise prepends
// `Bearer ` unless the token is already prefixed.
export function toBearerHeader(token) {
  if (!token) {
    return null;
  }
  return token.startsWith('Bearer ') ? token : `Bearer ${token}`;
}

// Reads a permission from an `rbac` prop object using the documented
// fall-open convention: missing rbac or missing key = allowed; only an
// explicit `false` denies. Use this anywhere `rbac?.canX !== false` would
// otherwise appear inline.
export function canPerform(rbac, key) {
  return rbac?.[key] !== false;
}

// Push `item` onto `menu` only when the user has the given verb on the URN.
// Collapses the boilerplate `if (can(verb, urn)) menu.push(item)` pattern in
// menu-building loops so each row is a single readable line.
export function pushIfAllowed(menu, can, verb, urn, item) {
  if (can(verb, urn)) {
    menu.push(item);
  }
}

// Per-URN permission fetch.
//
// POST {MATRIX_HOST}/api/v2/internal/permissions/get-resource-permission
//   body: { user: { userId, teamIds }, resourceUrn, source }
//   ok:   { data: { permissionLevels: [ "read", "write", ... ] } }
//
// Returns an array of lowercased level strings on success, null on
// transport/HTTP error or timeout (callers treat null as fall-open).
export async function getResourcePermissions(userId, urn, authHeader) {
  const host = getMatrixHost();
  if (!host || !userId || !urn || !authHeader) {
    return null;
  }

  // Bound the request so a hung matrix instance can't permanently freeze the
  // UI in the in-flight denied state -- the AbortError is caught below and
  // returns null, which the hook treats as fall-open.
  const controller = new AbortController();
  const timeoutId = setTimeout(
    () => controller.abort(),
    MATRIX_FETCH_TIMEOUT_MS
  );

  try {
    const resp = await fetch(
      `${host}/api/v2/internal/permissions/get-resource-permission`,
      {
        method: 'POST',
        headers: {
          Authorization: authHeader,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          user: { userId: String(userId), teamIds: [] },
          resourceUrn: urn,
          source: 'sdk'
        }),
        signal: controller.signal
      }
    );
    if (!resp.ok) {
      return null;
    }
    const json = await resp.json();
    const levels = Array.isArray(json?.data?.permissionLevels)
      ? json.data.permissionLevels
      : null;
    return levels ? levels.map(level => String(level).toLowerCase()) : null;
  } catch (error) {
    return null;
  } finally {
    clearTimeout(timeoutId);
  }
}

// Normalises the Amplitude FF payload. Accepts:
//   - boolean true                       (raw FF value)
//   - { variant: { key: 'true' } }       (Amplitude experiment shape)
//   - { variant: { key: 'on' } }         (alt Amplitude shape used by other ml flags)
// Anything else (false, missing, unknown variant) → false.
export function isRbacFlagOn(flagValue) {
  if (flagValue === true) {
    return true;
  }
  const variantKey = flagValue?.variant?.key;
  if (typeof variantKey === 'string') {
    const normalized = variantKey.toLowerCase();
    return normalized === 'true' || normalized === 'on';
  }
  return false;
}
